// NotificationToastListener — instant-тост по Socket.IO событию `notification:new`.
//
// Сервер (publishNotification) эмитит `notification:new` с title/message/actionUrl/type
// сразу после INSERT в `notifications` (гейтится тумблером «В приложении»). Этот
// слушатель показывает Sonner-тост немедленно, не дожидаясь PowerSync-синка строки
// в локальную SQLite (1-5с задержка). Сам счётчик непрочитанных и список в колоколе
// обновляются реактивно через PowerSync — здесь только мгновенный тост.
//
// Готовность AI-артефакта (конспект/карточки/тест/глоссарий/аудио/эссе/запись —
// type `ai_task_complete`) показывается как кликабельный `toast.success`
// «<артефакт> готов · Открыть» с переходом на страницу артефакта. Прочие типы
// (billing/legal/security/quota) — нейтральный `toast()` (без зелёной success-окраски),
// чтобы не выдавать предупреждение о квоте за «успех».
//
// targetUrl берётся из payload.actionUrl (его проставляет notifyArtifactReady /
// publishNotification на сервере — единый источник истины маршрута, как у колокола в
// notification-item.tsx). Внутренний путь → SPA-навигация, внешний URL → полная навигация.
//
// Монтируется один раз в __root.tsx рядом с <PushInit/>.

import { toast } from "@studyhub/ui/toast";
import { useNavigate, useRouterState } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { isWorkspaceRoute } from "@/components/layout/_layout-state";
import { isLocallyWatchingArtifact } from "@/hooks/artifact-watching-keys";
import { useMarkArtifactSeen } from "@/hooks/use-mark-artifact-seen";
import { useSocketEvent } from "@/hooks/use-socket-event";
import { isExternalUrl, parseActionUrl } from "@/lib/action-url";
import { isAppVisible } from "@/lib/app-visible";
import { isNotificationHiddenOnSurface } from "./app-hidden-types";

/**
 * Тип уведомления о готовности AI-артефакта. Совпадает с
 * `notificationTypeSchema` (packages/shared/contracts/notifications) для
 * `ai_task_complete` — именно его эмитит `notifyArtifactReady` для всех
 * генераторов (notes/flashcards/quiz/glossary/audio/essay/recording).
 */
const ARTIFACT_READY_TYPE = "ai_task_complete";

export function NotificationToastListener() {
	const navigate = useNavigate();
	const { t } = useTranslation("notifications");
	const markSeen = useMarkArtifactSeen();
	const pathname = useRouterState({ select: (s) => s.location.pathname });

	useSocketEvent("notification:new", (payload) => {
		if (isNotificationHiddenOnSurface(payload.type)) return;
		// Presence-aware ack: пользователь видит toast → гасим отложенный email/push
		// (гейт isAppVisible() внутри markSeen).
		if (payload.type === ARTIFACT_READY_TYPE && payload.groupKey) {
			markSeen(payload.groupKey, "toast");
		}

		// Уже на поверхности артефакта / в воркспейсе курса — прогресс на экране,
		// пачка «готово» сверху только мешает (особенно при генерации темы).
		if (
			payload.type === ARTIFACT_READY_TYPE &&
			isAppVisible() &&
			(isLocallyWatchingArtifact(payload.groupKey) || isWorkspaceRoute(pathname))
		) {
			return;
		}

		const url = payload.actionUrl;
		const action = url
			? {
					label: t("toast.open"),
					onClick: () => {
						// Внешний URL → полная навигация; внутренний путь → SPA (как в /notifications/$id).
						if (isExternalUrl(url)) window.location.href = url;
						else void navigate({ ...parseActionUrl(url) });
					},
				}
			: undefined;

		// Готовый AI-артефакт — заметный кликабельный success-тост «… готов · Открыть».
		// Прочие типы (квота/безопасность/биллинг) — нейтральный тост, без success-окраски.
		if (payload.type === ARTIFACT_READY_TYPE) {
			toast.success(payload.title, { description: payload.message, action });
			return;
		}

		toast(payload.title, { description: payload.message, action });
	});

	return null;
}
